import { useState } from 'react';
import { CustomerInfoType } from './useCustomerInfo';
import { BookingStep } from './booking-types';
import { Category } from './useCategorySelection';
import { Service } from './useServiceSelection';

// Define the booking data type
export interface BookingType {
  id: string;
  totalPrice: number;
}

/**
 * Custom hook to manage booking state data
 */
export const useBookingState = () => {
  // State for selected options
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [booking, setSelectedBooking] = useState<BookingType | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedTimeZone, setSelectedTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [serviceProviderMap, setServiceProviderMap] = useState({});
  const [customerInfo, setCustomerInfo] = useState<CustomerInfoType>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const handleSelectedLocation = (location: any) => {
    setSelectedLocation(location);
    setSelectedTimeZone(location.timeZone);
  };

  const handleBookingComplete = (bookingData: BookingType, setStepFn: (step: BookingStep) => void) => {
    setSelectedBooking(bookingData);
    setStepFn(BookingStep.BOOKING_CONFIRMED);
  };

  const resetState = (firstStep: BookingStep, setStepFn: (step: BookingStep) => void) => {
    // Reset current step
    setStepFn(firstStep);

    // Reset all the state values
    setSelectedServices([]);
    setSelectedProvider(null);
    setSelectedBooking(null);
    setSelectedDate(new Date());
    setSelectedTime(null);
    setSelectedLocation(null);
    setServiceProviderMap({});
    setSelectedTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    setCustomerInfo({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    });
  };

  return {
    // State values
    selectedServices,
    setSelectedServices,
    selectedProvider,
    setSelectedProvider,
    booking,
    setSelectedBooking,
    selectedDate,
    setSelectedDate,
    selectedTime,
    setSelectedTime,
    selectedLocation,
    selectedTimeZone,
    serviceProviderMap,
    setServiceProviderMap,
    customerInfo,
    setCustomerInfo,
    selectedCategories,
    setSelectedCategories,

    // Functions
    handleSelectedLocation,
    handleBookingComplete,
    resetState
  };
};

export default useBookingState;
